import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { RowDetailDrawer } from "@/components/search-work-orders/RowDetailDrawer";
import { SearchFiltersPanel } from "@/components/search-work-orders/SearchFiltersPanel";
import { SetValuesPanel } from "@/components/search-work-orders/SetValuesPanel";
import { WorkOrderTransferTable } from "@/components/search-work-orders/WorkOrderTransferTable";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import { queueMassUpdateJob } from "@/mocks/mass-update-jobs";
import { LOCATION_OPTIONS } from "@/mocks/mass-update-reference";
import {
  EMPTY_WORK_ORDER_SEARCH_FILTERS,
  EMPTY_WORK_ORDER_SET_VALUES,
  matchesWorkOrderSearchFilters,
  type WorkOrder,
  type WorkOrderSetValues,
} from "@/mocks/search-work-orders";

interface WorkOrdersWorkspaceProps {
  workOrders: WorkOrder[];
}

function toggleInSet(set: Set<string>, id: string, checked: boolean): Set<string> {
  const next = new Set(set);
  if (checked) next.add(id);
  else next.delete(id);
  return next;
}

function describeChanges(setValues: WorkOrderSetValues): string[] {
  const changes: string[] = [];
  if (setValues.status) changes.push(`Status → ${setValues.status}`);
  if (setValues.locationCode) {
    const location = LOCATION_OPTIONS.find((option) => option.value === setValues.locationCode);
    changes.push(`Location → ${location?.label ?? setValues.locationCode}`);
  }
  if (setValues.startDate) changes.push(`Start date → ${setValues.startDate}`);
  if (setValues.endDate) changes.push(`End date → ${setValues.endDate}`);
  if (setValues.firmed) changes.push(`Firmed → ${setValues.firmed === "true" ? "Yes" : "No"}`);
  if (setValues.memo) changes.push(`Memo → "${setValues.memo}"`);
  return changes;
}

export function WorkOrdersWorkspace({ workOrders }: WorkOrdersWorkspaceProps) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_WORK_ORDER_SEARCH_FILTERS);
  const [setValues, setSetValues] = useState(EMPTY_WORK_ORDER_SET_VALUES);
  const [hasSearched, setHasSearched] = useState(false);
  const [leftRows, setLeftRows] = useState<WorkOrder[]>([]);
  const [rightRows, setRightRows] = useState<WorkOrder[]>([]);
  const [leftChecked, setLeftChecked] = useState<Set<string>>(new Set());
  const [rightChecked, setRightChecked] = useState<Set<string>>(new Set());
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  function handleSearch() {
    const selectedIds = new Set(rightRows.map((row) => row.workOrderId));
    const matches = workOrders.filter(
      (candidate) =>
        matchesWorkOrderSearchFilters(candidate, filters) &&
        !selectedIds.has(candidate.workOrderId),
    );
    setLeftRows(matches);
    setLeftChecked(new Set());
    setHasSearched(true);
  }

  function handleClear() {
    setFilters(EMPTY_WORK_ORDER_SEARCH_FILTERS);
    setSetValues(EMPTY_WORK_ORDER_SET_VALUES);
    setLeftRows([]);
    setRightRows([]);
    setLeftChecked(new Set());
    setRightChecked(new Set());
    setHasSearched(false);
    setDrawerOpen(false);
  }

  function handleMoveRight() {
    const moving = leftRows.filter((row) => leftChecked.has(row.workOrderId));
    setLeftRows(leftRows.filter((row) => !leftChecked.has(row.workOrderId)));
    setRightRows([...rightRows, ...moving]);
    setLeftChecked(new Set());
  }

  function handleMoveLeft() {
    const moving = rightRows.filter((row) => rightChecked.has(row.workOrderId));
    setRightRows(rightRows.filter((row) => !rightChecked.has(row.workOrderId)));
    setLeftRows([...leftRows, ...moving]);
    setRightChecked(new Set());
  }

  function handleRowClick(row: WorkOrder) {
    setSelectedWorkOrder(row);
    setDrawerOpen(true);
  }

  function handleApplyConfirmed() {
    queueMassUpdateJob("Work Order Update", rightRows.length);
    toast.success(`Queued an update for ${rightRows.length} work order(s).`);
    setApplyOpen(false);
    navigate({ to: "/dashboard/jobs" });
  }

  const changes = describeChanges(setValues);
  const applyDisabled = rightRows.length === 0 || changes.length === 0;

  const leftEmptyMessage = hasSearched
    ? "No results yet — run a search above."
    : "Run a search to load matching work orders.";
  const leftCountText = leftRows.length
    ? `${leftRows.length} work order${leftRows.length === 1 ? "" : "s"} matched.`
    : leftEmptyMessage;
  const rightCountText = rightRows.length
    ? `${rightRows.length} work order${rightRows.length === 1 ? "" : "s"} selected.`
    : "Nothing selected yet.";

  return (
    <div className="space-y-6">
      <SearchFiltersPanel values={filters} onChange={setFilters} onSearch={handleSearch} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <WorkOrderTransferTable
          title="Search results"
          countText={leftCountText}
          rows={leftRows}
          emptyMessage={leftEmptyMessage}
          checkedIds={leftChecked}
          onToggleRow={(id, checked) => setLeftChecked(toggleInSet(leftChecked, id, checked))}
          onSelectAll={() => setLeftChecked(new Set(leftRows.map((row) => row.workOrderId)))}
          onSelectNone={() => setLeftChecked(new Set())}
          moveLabel="Add to selected →"
          moveDisabled={leftChecked.size === 0}
          onMove={handleMoveRight}
          onRowClick={handleRowClick}
        />

        <WorkOrderTransferTable
          title="Selected for update"
          countText={rightCountText}
          rows={rightRows}
          emptyMessage="Move records here from Search results to include them in the update."
          checkedIds={rightChecked}
          onToggleRow={(id, checked) => setRightChecked(toggleInSet(rightChecked, id, checked))}
          onSelectAll={() => setRightChecked(new Set(rightRows.map((row) => row.workOrderId)))}
          onSelectNone={() => setRightChecked(new Set())}
          moveLabel="← Remove"
          moveDisabled={rightChecked.size === 0}
          onMove={handleMoveLeft}
          onRowClick={handleRowClick}
          setValues={setValues}
        />
      </div>

      <SetValuesPanel
        values={setValues}
        onChange={setSetValues}
        selectedCount={rightRows.length}
        onClear={handleClear}
      />

      <div className="flex justify-end">
        <Button onClick={() => setApplyOpen(true)} disabled={applyDisabled}>
          Apply changes
        </Button>
      </div>

      <ConfirmDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        title={`Apply changes to ${rightRows.length} work order${rightRows.length === 1 ? "" : "s"}?`}
        description={
          changes.length
            ? `This will change: ${changes.join(", ")}. This queues a job — nothing in NetSuite is touched from this draft.`
            : "Set at least one field above before applying."
        }
        confirmLabel="Apply changes"
        onConfirm={handleApplyConfirmed}
      />

      <RowDetailDrawer
        workOrder={selectedWorkOrder}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}
